import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { keyBy, groupBy } from 'lodash'
import usePageVisibility from '../shared/hooks/usePageVisibility'

const REFRESH_INTERVAL_MS = 5 * 1000

export function createTeamResourceContext(config) {
  const {
    name,
    useResources,
    responseKey,
    requestOptions,
    lookups = [],
    groupings = [],
    computed = [],
    polling = true,
  } = config

  const Context = createContext(null)

  function Provider({ children }) {
    const { teamSlug } = useParams()
    const isVisible = usePageVisibility()
    const { all } = useResources(teamSlug)
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const loadingRef = useRef(false)
    const intervalRef = useRef(null)

    const load = useCallback(() => {
      if (loadingRef.current || !teamSlug) {
        return Promise.resolve()
      }
      loadingRef.current = true
      setError(null)
      return all(requestOptions)
        .then((response) => {
          setItems(response[responseKey])
          return response[responseKey]
        })
        .catch((err) => {
          setError(err)
          throw err
        })
        .finally(() => {
          setLoading(false)
          loadingRef.current = false
        })
    }, [teamSlug, all])

    const loadRef = useRef(load)
    loadRef.current = load

    // Initial load on teamSlug change
    useEffect(() => {
      if (!teamSlug) {
        setItems([])
        setLoading(false)
        return
      }

      setLoading(true)
      loadRef.current()
    }, [teamSlug])

    // Polling: start/stop based on visibility
    useEffect(() => {
      if (!polling || !teamSlug) {
        return
      }

      if (isVisible) {
        loadRef.current()
        intervalRef.current = setInterval(() => loadRef.current(), REFRESH_INTERVAL_MS)
      }

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }, [teamSlug, isVisible])

    const derivedValues = useMemo(() => {
      const result = {}

      lookups.forEach(({ key, field }) => {
        result[key] = keyBy(items, field)
      })

      groupings.forEach(({ key, field }) => {
        result[key] = groupBy(items, field)
      })

      computed.forEach(({ key, fn }) => {
        result[key] = fn(items)
      })

      return result
    }, [items])

    const value = useMemo(() => ({
      [responseKey]: items,
      ...derivedValues,
      loading,
      error,
      reload: load,
    }), [items, derivedValues, loading, error, load])

    return <Context.Provider value={value}>{children}</Context.Provider>
  }

  function useHook() {
    const context = useContext(Context)
    if (!context) {
      throw new Error(`use${name} must be used within a ${name}Provider`)
    }
    return context
  }

  function useSafeHook() {
    const context = useContext(Context)
    return context || { [responseKey]: [], loading: false, error: null, reload: () => Promise.resolve([]) }
  }

  return { Provider, useHook, useSafeHook }
}
